import React, { useCallback, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { Button, Table, Typography, List, Row, Col, notification } from 'antd'
import { Panel } from '../../../core/components/styled'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { formatScrapingTags } from '../../utils'
import { getAutoEatMenuUrl, getAutoEatDishReviewTaskUrl } from '../../helpers'
import { SCRAPING_TEST_CASES } from '../../graphql/queries'
import {
  MenuUrlFilter,
  MenuIdFilter,
  ScrapingTestCaseTagsModal,
  ScrapingTagsFilter,
} from './sections'
import { DELETE_SCRAPING_TEST_CASE } from '../../graphql/mutations'
import { formatDate } from '../../../core/utils/services/formatter'

export { formatDate } from '../../../core/utils/services/formatter'

const ScrapingTestCaseManagementPage = () => {
  useDocumentTitle('Scraping test case management')
  const history = useHistory()
  const { pathname, search } = useLocation()
  const [deletingTestCaseId, setDeletingTestCaseId] = useState(null)

  const [
    scrapingTestCaseTagsModalSettings,
    setScrapingscrapingTestCaseTagsModalSettings,
  ] = useState({
    testCaseId: null,
    isModalOpen: false,
  })

  const {
    tagIds,
    menuId,
    menuUrl,
    page = DEFAULT_PAGE,
  } = getQueryParams(search)

  const {
    data: { scrapingTestCases = {} } = {},
    loading: isLoadingTestCases,
    refetch: refetchScrapingTestCases,
  } = useQuery(SCRAPING_TEST_CASES, {
    variables: {
      filters: { tagIds, menuId, menuUrl },
      page,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const [deleteScrapingTestCase] = useMutation(DELETE_SCRAPING_TEST_CASE, {
    onCompleted() {
      setDeletingTestCaseId(null)
      refetchScrapingTestCases()
      notification.open({
        message: 'Test case was deleted.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Faled to delete test case.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const onDeleteScrapingTestCase = useCallback(
    id => {
      setDeletingTestCaseId(id)
      deleteScrapingTestCase({ variables: { id } })
    },
    [deleteScrapingTestCase],
  )

  const tableColumns = [
    {
      title: 'Menu ID',
      dataIndex: 'menuId',
      width: '100px',
      render: menuId => (
        <a
          href={`${getAutoEatMenuUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {menuId}
        </a>
      ),
    },
    {
      title: 'DR Task URL',
      dataIndex: 'menuId',
      width: '105px',
      render: menuId => (
        <a
          href={`${getAutoEatDishReviewTaskUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Link
        </a>
      ),
    },
    {
      title: 'Menu URL',
      dataIndex: 'menuUrl',
      ellipsis: true,
      render: menuUrl => (
        <a href={menuUrl} target="_blank" rel="noopener noreferrer">
          {menuUrl}
        </a>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      render: tags => (
        <List
          size="small"
          split={false}
          dataSource={formatScrapingTags(tags)}
          itemLayout="horizontal"
          renderItem={tag => (
            <List.Item style={{ padding: 0 }}>
              <Typography.Text>{tag}</Typography.Text>
            </List.Item>
          )}
        />
      ),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      width: '130px',
      render: formatDate,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      width: '130px',
      render: formatDate,
    },
    {
      align: 'right',
      width: '250px',
      render: scrapingTestCase => (
        <>
          <Button
            type="primary"
            style={{ marginRight: '10px' }}
            disabled={scrapingTestCase.id === deletingTestCaseId}
            onClick={() =>
              setScrapingscrapingTestCaseTagsModalSettings({
                testCaseId: scrapingTestCase.id,
                isModalOpen: true,
              })
            }
          >
            Configure tags
          </Button>
          <Button
            onClick={() => onDeleteScrapingTestCase(scrapingTestCase.id)}
            disabled={scrapingTestCase.id === deletingTestCaseId}
          >
            Delete
          </Button>
        </>
      ),
    },
  ]

  return (
    <>
      <Panel margin="small no">
        <Row gutter={24}>
          <Col span={3}>
            <MenuIdFilter />
          </Col>
          <Col span={5}>
            <MenuUrlFilter />
          </Col>
          <Col span={6}>
            <ScrapingTagsFilter />
          </Col>
        </Row>
      </Panel>
      <Table
        loading={isLoadingTestCases}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingTestCases.scrapingTestCases}
        size="small"
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          total: scrapingTestCases.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
        }}
      />

      <ScrapingTestCaseTagsModal
        {...scrapingTestCaseTagsModalSettings}
        refetchScrapingTestCases={refetchScrapingTestCases}
        closeModal={() =>
          setScrapingscrapingTestCaseTagsModalSettings({
            testCaseId: null,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}

export default ScrapingTestCaseManagementPage
